import { captureEvent } from './captureEvent'

export const DEFAULT_API_PERFORMANCE_THRESHOLD_MS = 5000

/**
 * How long an API call may take before it's logged as
 * `api-call-above-<threshold>`, from VITE_API_PERFORMANCE_THRESHOLD_MS, in
 * whole ms. Anything missing or invalid falls back to 5000.
 * @returns {number}
 */
export const getApiPerformanceThresholdMs = () => {
  // @ts-ignore
  const ms = Math.round(
    Number(import.meta.env.VITE_API_PERFORMANCE_THRESHOLD_MS),
  )

  return Number.isFinite(ms) && ms > 0
    ? ms
    : DEFAULT_API_PERFORMANCE_THRESHOLD_MS
}

/**
 * Our own APIs. turbodebt.com and the current origin also serve pages and
 * static files, so only their /api/ paths count.
 * @returns {RegExp[]}
 */
export const getApiUrlPatterns = () => [
  /^https:\/\/(?:.+\.)?trbotrkr\.com\//,
  /^https:\/\/(?:.+\.)?turbodevhub\.com\//,
  /^https:\/\/(?:.+\.)?turbodebt\.com\/api\//,
  new RegExp(
    `^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/api/`,
  ),
]

const API_INITIATORS = ['fetch', 'xmlhttprequest']

/**
 * Logs a finished request as `api-call-above-<threshold>` when it's one of
 * our API calls and took longer than the threshold
 *
 * @param {PerformanceResourceTiming} entry
 * @param {number} threshold - ms
 * @param {RegExp[]} patterns - which URLs are API calls
 */
export function captureIfSlowApiCall(entry, threshold, patterns) {
  if (
    entry.duration < threshold ||
    !API_INITIATORS.includes(entry.initiatorType) ||
    !patterns.some((pattern) => pattern.test(entry.name))
  ) {
    return
  }

  const url = new URL(entry.name)

  captureEvent(`api-call-above-${threshold}`, {
    endpoint: url.pathname,
    host: url.host,
    duration_ms: Math.round(entry.duration),
    threshold_ms: threshold,
    // only some browsers expose the status of a resource entry
    ...(entry.responseStatus ? { status: entry.responseStatus } : {}),
  })
}

/**
 * Watches every request the page makes and logs our slow API calls, so no
 * call site has to time itself. Requests made before this runs are included.
 */
export function observeSlowApiCalls() {
  if (
    typeof PerformanceObserver === 'undefined' ||
    !PerformanceObserver.supportedEntryTypes?.includes('resource')
  ) {
    return
  }

  const threshold = getApiPerformanceThresholdMs()
  const patterns = getApiUrlPatterns()

  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        captureIfSlowApiCall(
          /** @type {PerformanceResourceTiming} */ (entry),
          threshold,
          patterns,
        )
      })
    }).observe({ type: 'resource', buffered: true })
  } catch (error) {
    console.warn('Slow API call tracking failed to start', error)
  }
}
